import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  X, ArrowRight, ArrowLeft, Check, Target, Calendar, 
  Plane, Car, Home, GraduationCap, Heart, Sparkles,
  TrendingUp, PiggyBank, Gift, Wallet, Zap, Clock,
  HandCoins, Repeat
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import FrequencySelector from '@/components/ui/FrequencySelector';
import { 
  GOAL_FREQUENCIES, 
  getFrequencyByValue, 
  getFrequencyLabel,
  calculateCustomMultiplier,
  CUSTOM_FREQUENCY_UNITS,
  requiresDayOfMonth
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Suggestions d'objectifs avec icônes et montants
const SUGGESTIONS = [
  { name: '🎂 Anniversaire', icon: Gift, color: 'from-pink-400 to-rose-500', defaultAmount: 100, isBirthday: true },
  { name: 'Voyage de rêve', icon: Plane, color: 'from-cyan-400 to-blue-500', defaultAmount: 2500 },
  { name: 'Nouvelle voiture', icon: Car, color: 'from-slate-400 to-gray-600', defaultAmount: 15000 },
  { name: 'Apport maison', icon: Home, color: 'from-emerald-400 to-teal-500', defaultAmount: 20000 },
  { name: 'Études / Formation', icon: GraduationCap, color: 'from-violet-400 to-purple-500', defaultAmount: 5000 },
  { name: 'Mariage', icon: Heart, color: 'from-pink-400 to-rose-500', defaultAmount: 10000 },
  { name: 'Cadeau spécial', icon: Gift, color: 'from-amber-400 to-orange-500', defaultAmount: 500 },
  { name: 'Fonds d\'urgence', icon: Wallet, color: 'from-red-400 to-rose-600', defaultAmount: 3000 },
  { name: 'Projet personnel', icon: Sparkles, color: 'from-indigo-400 to-blue-500', defaultAmount: 2000 },
];

// Modes d'alimentation
const MODES = [
  { 
    value: 'automatic', 
    label: 'Automatique', 
    desc: 'Versements récurrents programmés',
    icon: Repeat,
    color: 'from-emerald-500 to-teal-600',
    detail: 'Idéal pour atteindre ton objectif sans y penser'
  },
  { 
    value: 'manual', 
    label: 'Manuel', 
    desc: 'Tu décides quand verser',
    icon: HandCoins,
    color: 'from-blue-500 to-indigo-600',
    detail: 'Plus de flexibilité, tu verses quand tu veux'
  },
];

const CreateSavingsGoalWizard = ({ open, onClose, calendarId, currentBalance, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    selectedSuggestion: null,
    targetAmount: '',
    mode: 'automatic',
    contributionAmount: '',
    // Système de fréquence harmonisé
    contributionFrequency: 'mensuel',
    frequencyUnit: 'mois',
    frequencyValue: 1,
    contributionDay: new Date().getDate(),
    startDate: new Date().toISOString().split('T')[0],
    hasDeadline: false,
    targetDate: ''
  });

  const totalSteps = formData.mode === 'automatic' ? 4 : 3;

  // Reset form when closing
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setShowSuccess(false);
        setFormData({
          name: '',
          selectedSuggestion: null,
          targetAmount: '',
          mode: 'automatic',
          contributionAmount: '',
          contributionFrequency: 'mensuel',
          frequencyUnit: 'mois',
          frequencyValue: 1,
          contributionDay: new Date().getDate(),
          startDate: new Date().toISOString().split('T')[0],
          hasDeadline: false,
          targetDate: ''
        });
      }, 300);
    }
  }, [open]);

  // Calculs dynamiques - système harmonisé
  const calculations = useMemo(() => {
    const target = parseFloat(formData.targetAmount) || 0;
    const contribution = parseFloat(formData.contributionAmount) || 0;
    
    // Calcul du multiplicateur mensuel selon la fréquence
    let monthlyMultiplier = 1;
    
    if (formData.contributionFrequency === 'personnalise') {
      // Fréquence personnalisée
      monthlyMultiplier = calculateCustomMultiplier(formData.frequencyUnit, formData.frequencyValue);
    } else {
      // Fréquence standard
      const freq = getFrequencyByValue(formData.contributionFrequency);
      monthlyMultiplier = freq?.multiplierToMonthly || 1;
    }
    
    const monthlyContribution = contribution * monthlyMultiplier;
    
    let monthsToGoal = 0;
    let estimatedDate = null;
    
    if (monthlyContribution > 0 && target > 0) {
      monthsToGoal = Math.ceil(target / monthlyContribution);
      const date = new Date();
      date.setMonth(date.getMonth() + monthsToGoal);
      estimatedDate = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
    
    const impactMensuel = -monthlyContribution;
    const nouveauSolde = (currentBalance || 0) + impactMensuel;
    
    return {
      monthlyContribution,
      monthsToGoal,
      estimatedDate,
      impactMensuel,
      nouveauSolde,
      yearlyContribution: monthlyContribution * 12,
      monthlyMultiplier
    };
  }, [formData.targetAmount, formData.contributionAmount, formData.contributionFrequency, formData.frequencyUnit, formData.frequencyValue, currentBalance]);

  const handleSuggestionClick = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      name: suggestion.name,
      targetAmount: suggestion.defaultAmount.toString(),
      selectedSuggestion: suggestion
    }));
  };

  const handleModeSelect = (mode) => {
    setFormData(prev => ({ ...prev, mode: mode.value }));
  };

  // Handler pour le changement de fréquence (système harmonisé)
  const handleFrequencyChange = (frequencyType, frequencyUnit, frequencyValue) => {
    setFormData(prev => ({
      ...prev,
      contributionFrequency: frequencyType,
      frequencyUnit: frequencyUnit,
      frequencyValue: frequencyValue
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.name.trim().length > 0;
      case 2: return parseFloat(formData.targetAmount) > 0;
      case 3: 
        if (formData.mode === 'automatic') {
          return parseFloat(formData.contributionAmount) > 0;
        }
        return true;
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        target_amount: parseFloat(formData.targetAmount),
        start_date: formData.startDate,
        target_date: formData.hasDeadline && formData.targetDate ? formData.targetDate : null,
        mode: formData.mode,
      };

      if (formData.mode === 'automatic') {
        payload.contribution_amount = parseFloat(formData.contributionAmount);
        payload.contribution_day = formData.contributionDay;
        payload.contribution_category = 'Épargne';
        // Système de fréquence harmonisé
        payload.contribution_frequency = formData.contributionFrequency;
        payload.frequency_unit = formData.frequencyUnit;
        payload.frequency_value = formData.frequencyValue;
      }

      await axios.post(
        `${API}/savings-goals?calendar_id=${calendarId}`,
        payload,
        { withCredentials: true }
      );

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2500);

    } catch (error) {
      console.error('Error creating savings goal:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    if (formData.selectedSuggestion) {
      return formData.selectedSuggestion.icon;
    }
    return Target;
  };

  const getGradient = () => {
    if (formData.selectedSuggestion) {
      return formData.selectedSuggestion.color;
    }
    return 'from-emerald-400 to-teal-500';
  };

  if (!open) return null;

  const IconComponent = getIcon();

  // Animation variants
  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? 100 : -100, opacity: 0 })
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        {/* Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full sm:max-w-md max-h-[95vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Success Animation */}
          {showSuccess ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${getGradient()} flex items-center justify-center mb-6`}
              >
                <Check className="h-12 w-12 text-white" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                Bravo ! 🎯
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-gray-600 mb-2"
              >
                Ton objectif "{formData.name}" est créé !
              </motion.p>
              {formData.mode === 'automatic' && calculations.estimatedDate && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-sm text-emerald-600 font-medium"
                >
                  🗓️ Estimation : {calculations.estimatedDate}
                </motion.p>
              )}
            </motion.div>
          ) : (
            <>
              {/* Header */}
              <div className={`bg-gradient-to-r ${getGradient()} p-4 sticky top-0 z-10`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      key={formData.selectedSuggestion?.name || 'default'}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring' }}
                      className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"
                    >
                      <IconComponent className="h-5 w-5 text-white" />
                    </motion.div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Nouvel objectif d'épargne</h2>
                      <p className="text-xs text-white/70">Étape {step} sur {totalSteps}</p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-white"
                    initial={{ width: 0 }}
                    animate={{ width: `${(step / totalSteps) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Steps Content */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
                <AnimatePresence mode="wait" custom={step}>
                  {/* Step 1: Name & Project Selection */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Quel est ton prochain grand projet ? 🚀</h3>
                        <p className="text-sm text-gray-500">
                          Un objectif concret te motivera à épargner régulièrement
                        </p>
                      </div>

                      {/* Suggestions grid */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {SUGGESTIONS.map((suggestion) => (
                          <motion.button
                            key={suggestion.name}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                              formData.name === suggestion.name 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-gray-200 hover:border-emerald-300'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${suggestion.color} flex items-center justify-center flex-shrink-0`}>
                              <suggestion.icon className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate">{suggestion.name}</span>
                          </motion.button>
                        ))}
                      </div>

                      {/* Custom name input */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Ou personnalise ton objectif
                        </label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value, selectedSuggestion: null }))}
                          placeholder="Ex : Voyage, nouvel ordi, projet perso..."
                          className="text-base"
                          data-testid="goal-name-input"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Target Amount & Mode */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Combien veux-tu économiser ?</h3>
                        <p className="text-sm text-gray-500">
                          Définis le montant total de ton objectif
                        </p>
                      </div>

                      {/* Target amount input */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 mb-6">
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.targetAmount}
                            onChange={(e) => setFormData(prev => ({ ...prev, targetAmount: e.target.value }))}
                            placeholder="0"
                            className="text-4xl font-bold text-center border-0 bg-transparent focus-visible:ring-0 text-gray-900 placeholder:text-gray-300"
                            data-testid="target-amount-input"
                          />
                          <div className="text-center text-gray-500 mt-1">euros</div>
                        </div>
                        
                        {/* Quick amounts */}
                        <div className="flex justify-center gap-2 mt-4">
                          {[500, 1000, 2000, 5000].map((amount) => (
                            <button
                              key={amount}
                              onClick={() => setFormData(prev => ({ ...prev, targetAmount: amount.toString() }))}
                              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                formData.targetAmount === amount.toString()
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-white text-gray-600 hover:bg-emerald-50'
                              }`}
                            >
                              {amount}€
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Mode selection */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">Comment veux-tu épargner ?</h4>
                        {MODES.map((mode) => (
                          <motion.button
                            key={mode.value}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() => handleModeSelect(mode)}
                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                              formData.mode === mode.value 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-gray-200 hover:border-emerald-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mode.color} flex items-center justify-center`}>
                                <mode.icon className="h-6 w-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{mode.label}</h4>
                                <p className="text-sm text-gray-500">{mode.desc}</p>
                              </div>
                              {formData.mode === mode.value && (
                                <Check className="h-5 w-5 text-emerald-600" />
                              )}
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Contribution Setup (for automatic mode) OR Confirmation (for manual) */}
                  {step === 3 && formData.mode === 'automatic' && (
                    <motion.div
                      key="step3-auto"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Combien par versement ?</h3>
                        <p className="text-sm text-gray-500">
                          Définis le montant et la fréquence de tes versements automatiques
                        </p>
                      </div>

                      {/* Contribution amount */}
                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6">
                        <div className="relative mb-4">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.contributionAmount}
                            onChange={(e) => setFormData(prev => ({ ...prev, contributionAmount: e.target.value }))}
                            placeholder="0"
                            className="text-3xl font-bold text-center border-0 bg-transparent focus-visible:ring-0 text-gray-900 placeholder:text-gray-300"
                            data-testid="contribution-amount-input"
                          />
                          <div className="text-center text-gray-500 text-sm">euros par versement</div>
                        </div>

                        {/* Quick amounts */}
                        <div className="flex justify-center gap-2 mb-4">
                          {[25, 50, 100, 200].map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, contributionAmount: amount.toString() }))}
                              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                formData.contributionAmount === amount.toString()
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-white text-gray-600 hover:bg-emerald-100'
                              }`}
                            >
                              {amount}€
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Frequency selection - SYSTÈME HARMONISÉ */}
                      <FrequencySelector
                        value={formData.contributionFrequency}
                        onChange={handleFrequencyChange}
                        frequencyUnit={formData.frequencyUnit}
                        frequencyValue={formData.frequencyValue}
                        variant="full"
                        colorScheme="emerald"
                        allowedFrequencies={GOAL_FREQUENCIES.map(f => f.value)}
                        showDescription={true}
                      />

                      {/* Projection */}
                      {calculations.monthsToGoal > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white border border-emerald-200 rounded-xl p-4"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="h-5 w-5 text-emerald-600" />
                            <span className="font-semibold text-gray-900">Ta projection</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-emerald-50 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-emerald-600">{calculations.monthsToGoal}</div>
                              <div className="text-gray-600">mois estimés</div>
                            </div>
                            <div className="bg-teal-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-teal-600">{calculations.estimatedDate}</div>
                              <div className="text-gray-600">date estimée</div>
                            </div>
                          </div>
                          {/* Détail du calcul */}
                          <div className="mt-3 pt-3 border-t border-emerald-100 text-center">
                            <p className="text-xs text-gray-500">
                              {calculations.monthlyContribution.toFixed(0)}€ / mois × {calculations.monthsToGoal} mois
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                  {/* Step 3 for manual mode - Confirmation */}
                  {step === 3 && formData.mode === 'manual' && (
                    <motion.div
                      key="step3-manual"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Récapitulatif</h3>
                        <p className="text-sm text-gray-500">
                          Vérifie les détails de ton objectif
                        </p>
                      </div>

                      {/* Summary card */}
                      <div className={`bg-gradient-to-br ${getGradient()} rounded-2xl p-6 text-white`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                            <IconComponent className="h-7 w-7" />
                          </div>
                          <div>
                            <h4 className="font-bold text-xl">{formData.name}</h4>
                            <p className="text-white/70 text-sm">Versements manuels</p>
                          </div>
                        </div>
                        
                        <div className="bg-white/10 rounded-xl p-4">
                          <div className="text-center">
                            <div className="text-4xl font-bold">{parseFloat(formData.targetAmount).toLocaleString('fr-FR')}€</div>
                            <div className="text-white/70 text-sm mt-1">Objectif à atteindre</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <HandCoins className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-900">Mode manuel sélectionné</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Tu pourras faire des versements quand tu le souhaites depuis l'écran Épargne.
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: Final Configuration (automatic mode only) */}
                  {step === 4 && formData.mode === 'automatic' && (
                    <motion.div
                      key="step4"
                      custom={1}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Derniers réglages</h3>
                        <p className="text-sm text-gray-500">
                          Choisis quand commencer et le jour de prélèvement
                        </p>
                      </div>

                      {/* Start date */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 block">Date de début</label>
                        <Input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                          className="text-base"
                        />
                      </div>

                      {/* Day of month - Only show for frequencies that need it */}
                      {requiresDayOfMonth(formData.contributionFrequency, formData.frequencyUnit) && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 block">Jour du prélèvement</label>
                          <div className="flex flex-wrap gap-2">
                            {[1, 5, 10, 15, 20, 25].map((day) => (
                              <button
                                key={day}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, contributionDay: day }))}
                                className={`w-12 h-12 rounded-xl font-semibold transition-colors ${
                                  formData.contributionDay === day
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-emerald-50'
                                }`}
                              >
                                {day}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">
                            Le {formData.contributionDay} de chaque période
                          </p>
                        </div>
                      )}

                      {/* Final summary */}
                      <div className={`bg-gradient-to-br ${getGradient()} rounded-2xl p-5 text-white mt-6`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <IconComponent className="h-6 w-6" />
                          </div>
                          <div>
                            <h4 className="font-bold">{formData.name}</h4>
                            <p className="text-white/70 text-sm">Objectif : {parseFloat(formData.targetAmount).toLocaleString('fr-FR')}€</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white/10 rounded-xl p-3 text-center">
                            <div className="text-xl font-bold">{formData.contributionAmount}€</div>
                            <div className="text-xs text-white/70">
                              {getFrequencyLabel(formData.contributionFrequency, formData.frequencyUnit, formData.frequencyValue)}
                            </div>
                          </div>
                          <div className="bg-white/10 rounded-xl p-3 text-center">
                            <div className="text-xl font-bold">{calculations.monthsToGoal}</div>
                            <div className="text-xs text-white/70">mois estimés</div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="p-4 border-t bg-gray-50 sticky bottom-0">
                <div className="flex gap-3">
                  {step > 1 && (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Retour
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className={`flex-1 bg-gradient-to-r ${getGradient()} hover:opacity-90`}
                    data-testid="next-step-button"
                  >
                    {loading ? (
                      'Création...'
                    ) : step === totalSteps ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Créer l'objectif
                      </>
                    ) : (
                      <>
                        Continuer
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateSavingsGoalWizard;
